import { z } from "zod";

export const discoveryConfigSchema = z
  .object({
    version: z.string(),
    localRadiusKm: z.number().positive(),
    expandedRadiusKm: z.number().positive(),
    activeMarkets: z.array(z.literal("mumbai")).min(1),
    minimumLocalCards: z.number().int().positive(),
    classTimeWindowMinutes: z.number().int().positive(),
    bookableLookaheadHours: z.number().positive(),
    venueCandidateCap: z.number().int().positive().max(100),
    activityWeight: z.number().nonnegative(),
    distanceWeight: z.number().nonnegative(),
    membershipWeight: z.number().nonnegative(),
    /**
     * Shared music on profiles (0..1 affinity × this). Kept well under the
     * activity and embedding terms so music enhances, never dominates, and
     * it is additive-only: members without music lose nothing.
     */
    musicWeight: z.number().nonnegative().max(20),
    behavioralLookbackDays: z.number().positive(),
    behavioralHistoryLimit: z.number().int().positive().max(200),
    behavioralHalfLifeDays: z.number().positive(),
    behavioralMaxContribution: z.number().positive(),
    exposurePenalty: z.number().nonnegative(),
    maxExposurePenalty: z.number().nonnegative(),
    exposureHalfLifeHours: z.number().positive(),
    exposureDebounceMinutes: z.number().positive(),
    // Recommended people: bounded seeded variation, never event/class chronology.
    explorationFraction: z.number().min(0).max(0.2),
    maxExploreScoreGap: z.number().nonnegative(),
    impressionVisibleRatio: z.number().min(0).max(1),
    impressionDwellMs: z.number().int().positive(),
    redisMaxEntities: z.number().int().positive().max(500),
    redisRetentionDays: z.number().positive(),
    redisIdleTtlDays: z.number().positive(),
    redisReadTimeoutMs: z.number().int().positive(),
    snapshotTtlMinutes: z.number().positive(),
    exposureBatchSize: z.number().int().positive().max(20),
    exposureFlushMs: z.number().int().positive(),
  })
  .refine(
    (c) =>
      c.expandedRadiusKm >= c.localRadiusKm &&
      c.redisIdleTtlDays >= c.redisRetentionDays,
  );

/** Numeric policy controls; never overrides privacy, eligibility or chronology. */
export const discoveryConfig = discoveryConfigSchema.parse({
  version: "discovery-v1",
  localRadiusKm: 50,
  expandedRadiusKm: 100,
  activeMarkets: ["mumbai"],
  minimumLocalCards: 3,
  classTimeWindowMinutes: 120,
  bookableLookaheadHours: 48,
  venueCandidateCap: 56,
  activityWeight: 45,
  distanceWeight: 25,
  membershipWeight: 10,
  musicWeight: 12,
  behavioralLookbackDays: 180,
  behavioralHistoryLimit: 100,
  behavioralHalfLifeDays: 60,
  behavioralMaxContribution: 0.6,
  exposurePenalty: 3,
  maxExposurePenalty: 9,
  exposureHalfLifeHours: 24,
  exposureDebounceMinutes: 30,
  explorationFraction: 0.1,
  maxExploreScoreGap: 10,
  impressionVisibleRatio: 0.5,
  impressionDwellMs: 1000,
  redisMaxEntities: 500,
  redisRetentionDays: 7,
  redisIdleTtlDays: 14,
  redisReadTimeoutMs: 100,
  snapshotTtlMinutes: 30,
  exposureBatchSize: 20,
  exposureFlushMs: 2000,
});
